/**
 * WuaRankChip — surfaces the WUA Native Outcomes Rank on
 * UniversityDetail. Silent for unis where we don't have enough
 * signals (needs at least 3 of: research, employment, salary,
 * recruitment, selectivity). Click opens a breakdown popover showing
 * each weighted signal + full methodology note.
 */
import { useEffect, useState } from "react";
import { Trophy, X } from "lucide-react";
import { api } from "@/lib/api";

const BAND_STYLE = {
  "world-elite": { label: "WUA World-Elite", ring: "border-amber-400 bg-amber-50 text-amber-900" },
  elite:         { label: "WUA Elite",       ring: "border-emerald-400 bg-emerald-50 text-emerald-800" },
  strong:        { label: "WUA Strong",      ring: "border-sky-400 bg-sky-50 text-sky-800" },
  regular:       { label: "WUA Regular",     ring: "border-stone-300 bg-stone-50 text-stone-700" },
  emerging:      { label: "WUA Emerging",    ring: "border-stone-300 bg-stone-50 text-stone-700" },
};

const SIGNAL_LABEL = {
  research:    "Research (OpenAlex h-index)",
  employment:  "Grad employment %",
  salary:      "Grad salary (yr 1)",
  recruitment: "Top-employer pipelines",
  selectivity: "Selectivity (1 − accept rate)",
};

export default function WuaRankChip({ uniId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!uniId) return;
    api.get(`/ranking/wua-outcomes/${uniId}`)
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [uniId]);
  if (!data || data.score == null) return null;
  const style = BAND_STYLE[data.band] || BAND_STYLE.regular;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="wua-rank-chip"
        className={
          "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-underground-sc tracking-wider hover:shadow-sm transition " +
          style.ring
        }
        title="WUA Native Outcomes Rank · click for breakdown"
      >
        <Trophy className="h-3 w-3" aria-hidden />
        {style.label} · {data.score}/100
      </button>
      {open && (
        <div
          data-testid="wua-rank-modal"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-stone-900/40 p-4"
          onClick={() => setOpen(false)}
        >
          <div className="bg-white rounded-t-xl sm:rounded-xl max-w-lg w-full p-6 shadow-2xl relative"
               onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOpen(false)}
                    className="absolute top-3 right-3 text-stone-400 hover:text-stone-700"
                    aria-label="Close"
                    data-testid="wua-rank-modal-close">
              <X className="h-4 w-4" />
            </button>
            <div className="text-[10px] font-underground-sc tracking-[0.18em] text-stone-500">
              WUA Native Outcomes Rank
            </div>
            <h3 className="mt-1 font-editorial text-2xl text-[#002147]">
              {data.name} — {style.label.toLowerCase()} · {data.score}/100
            </h3>
            <div className="mt-4 space-y-2">
              {Object.entries(data.breakdown).map(([k, v]) => (
                <div key={k}
                     data-testid={`wua-rank-signal-${k}`}
                     className="text-[13px]">
                  <div className="flex justify-between items-baseline text-stone-700">
                    <span>{SIGNAL_LABEL[k]}</span>
                    <span className="tabular-nums font-mono">
                      {v == null ? "—" : `${v}/100`}
                      <span className="ml-2 text-stone-400">
                        · {Math.round((data.methodology?.weights?.[k] || 0) * 100)} %
                      </span>
                    </span>
                  </div>
                  <div className="mt-0.5 h-1.5 rounded-full bg-stone-100 overflow-hidden">
                    <div className="h-full bg-[#4C1D95]"
                         style={{ width: `${v == null ? 0 : v}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[11px] text-stone-500 leading-snug">
              {data.methodology?.note}
            </p>
          </div>
        </div>
      )}
    </>
  );
}
